import type { AppThemeComponents, OverrideableAppThemeContextProps } from './AppTheme';
import type { Theme} from '@mui/material/styles';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import * as React from 'react';

import { AppThemeContext } from './AppTheme';

import { setLayoutDimension } from '@egr/xbox/telemetry/MatomoUtils';
import { layoutInformation } from '@egr/xbox/utils/Layout';

type ThemeProviderProps = React.PropsWithChildren<{
    darkTheme: Theme;
    lightTheme: Theme;
} & Partial<OverrideableAppThemeContextProps> & AppThemeComponents>;

const muiCache = createCache({
    key: 'mui',
    prepend: true
});

muiCache.compat = true; // disabled SSR-Warnings

export const AppThemeProvider: React.FC<ThemeProviderProps> = (props) => {
    const darkmode = layoutInformation.useDarkmodeActive();
    React.useEffect(
        () => {
            const breakpoints = (darkmode ? props.darkTheme : props.lightTheme).breakpoints;
            setLayoutDimension(darkmode);
            layoutInformation.setBreakpoints({
                sm: breakpoints.values.sm,
                md: breakpoints.values.md,
            });
        },
        [
            darkmode,
            props.darkTheme,
            props.lightTheme,
        ]
    );

    const activeTheme: Theme = darkmode ? props.darkTheme : props.lightTheme;

    return (
        <AppThemeContext.Provider
            value={{
                disableMuiTooltips: props.disableMuiTooltips ?? false,
                imagePlaceholder: props.imagePlaceholder ?? null,
                loadingImagePlaceholder: props.loadingImagePlaceholder ?? null,
                productPlaceholder: props.productPlaceholder ?? null,
                projectPlaceholder: props.projectPlaceholder ?? null,
                useBottomSheet: props.useBottomSheet ?? false,
                hideLogoInArticleList: props.hideLogoInArticleList ?? false,
                greyGDIcons: props.greyGDIcons ?? false,
                disableLazyLoadImage: props.disableLazyLoadImage ?? false,
                thinIcons: props.thinIcons ?? false,
                dialogVariant: props.dialogVariant ?? 'default',
            }}
        >
            <CacheProvider value={muiCache}>
                <ThemeProvider theme={activeTheme}>
                    <CssBaseline>
                        {props.children}
                    </CssBaseline>
                </ThemeProvider>
            </CacheProvider>
        </AppThemeContext.Provider>
    );
};

AppThemeProvider.displayName = 'AppThemeProvider';